import type { EditionSummary } from '@koderek/shared';
import { Anchor, Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { Link } from 'react-router';
import { IconChevronRight, IconClock, IconMapPin } from '../../components/icons';
import { useMe } from '../auth/session';
import { t } from '../../texts/pl';
import classes from './EditionCard.module.css';
import { dayAndMonth, formatDate, formatDateTime, formatTime } from './format';

/**
 * Stan zapisów edycji (US-104 AC-1, US-102 AC-4). `color` – wszystkie znaczki w jednym kolorze (karta edycji: kolor edycji,
 * żeby karta nie była pstrokata); bez niego każdy stan ma swój kolor.
 */
export function RegistrationBadges({ edition, color }: { edition: EditionSummary; color?: string }) {
  const c = (own: string) => color ?? own;
  const variant = color ? 'light' : undefined;
  return (
    <Group gap="xs">
      {edition.status === 'draft' ? <Badge color="gray">{t.editions.draft}</Badge> : null}
      {edition.status === 'finished' ? <Badge color="gray">{t.editions.finished}</Badge> : null}
      {edition.status === 'published' ? (
        edition.registrationOpen ? (
          <Badge color={c('green')} variant={variant}>
            {t.editions.registrationOpen}
          </Badge>
        ) : (
          <Badge color={c('blue')} variant={variant}>
            {t.editions.registrationFrom(formatDateTime(edition.registrationOpensAt))}
          </Badge>
        )
      ) : null}
      {edition.earlyAccess && !edition.registrationOpen ? (
        <Badge color={c('violet')} variant={variant}>
          {t.editions.earlyAccess}
        </Badge>
      ) : null}
      {edition.registered ? (
        <Badge color={c('teal')} variant={variant}>
          {t.editions.registered}
        </Badge>
      ) : null}
    </Group>
  );
}

/**
 * Kolor karty (PRD §10 pkt 44): wybrany dla edycji, a bez niego – kolor marki przy otwartych zapisach, niebieski przed
 * otwarciem, szary po edycji. Z niego wszystko na karcie: pasek, ramka, znaczki, ikony, kafelek daty, przyciski
 * (`EditionCard.module.css` dostaje go jako `--edition-accent`).
 */
function accent(edition: EditionSummary): string {
  if (edition.accentColor) return edition.accentColor;
  if (edition.status !== 'published') return 'gray';
  return edition.registrationOpen ? 'brand' : 'blue';
}

/** Kolor Mantine (nazwa z motywu albo `#rrggbb`) jako wartość CSS. */
function cssColor(color: string): string {
  return color.startsWith('#') ? color : `var(--mantine-color-${color}-6)`;
}

/**
 * `inDay` – karta w grupie kilku edycji tego samego dnia (np. jedna konferencja w kilku miastach): data jest w nagłówku
 * grupy, więc na karcie tylko godziny (tytułem zawsze nazwa edycji, miasto w adresie).
 */
export function EditionCard({ edition, inDay = false }: { edition: EditionSummary; inDay?: boolean }) {
  const to = `/edycje/${edition.slug}`;
  const { day, month } = dayAndMonth(edition.date);
  // Zalogowany z rodziną (jak menu rodzica – bez samej obsługi) przy otwartych zapisach albo wczesnym dostępie: zachęta do zapisu.
  const roles = useMe().data?.roles;
  const canEnroll =
    !!roles &&
    (roles.includes('admin') || !roles.includes('staff')) &&
    edition.status === 'published' &&
    (edition.registrationOpen || edition.earlyAccess);
  const color = accent(edition);
  return (
    <Paper radius="md" className={classes.card} style={{ '--edition-accent': cssColor(color) } as React.CSSProperties}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Stack gap="sm" miw={0}>
          <RegistrationBadges edition={edition} color={color} />
          <Title order={2} size="h3">
            {edition.name}
          </Title>
        </Stack>
        {inDay ? null : (
          <div className={classes.dateTile} title={formatDate(edition.date)}>
            <span className={classes.day}>{day}</span>
            <span className={classes.month}>{month}</span>
          </div>
        )}
      </Group>
      <Group gap="xl" mt="md" c="dimmed" style={{ rowGap: 8 }}>
        <Group gap={6} wrap="nowrap">
          <IconMapPin size={18} className={classes.icon} />
          {edition.mapUrl ? (
            <Anchor
              href={edition.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              c="inherit"
              title={t.editions.map}
            >
              {edition.city}, {edition.address}
            </Anchor>
          ) : (
            <Text size="sm">
              {edition.city}, {edition.address}
            </Text>
          )}
        </Group>
        <Group gap={6} wrap="nowrap">
          <IconClock size={18} className={classes.icon} />
          <Text size="sm">
            {inDay ? '' : `${formatDate(edition.date)}, `}
            {formatTime(edition.startsAt)}–{formatTime(edition.endsAt)}
          </Text>
        </Group>
      </Group>
      <Group gap="xs" mt="lg">
        {/* Kolor edycji (PRD §10 pkt 44) także na przyciskach; `autoContrast` – ciemny napis na jasnym kolorze. */}
        <Button
          component={Link}
          to={to}
          color={color}
          autoContrast
          rightSection={<IconChevronRight size={16} />}
        >
          {canEnroll ? t.editions.enroll : t.editions.open}
        </Button>
        {edition.registered ? (
          <Button
            component={Link}
            to={`/zajecia-rodziny?edycja=${edition.slug}`}
            variant="light"
            color={color}
          >
            {t.editions.myPlan}
          </Button>
        ) : null}
      </Group>
    </Paper>
  );
}
